import { useEffect, useState } from "react";
import axios from "axios";
import { Box, Button, Checkbox, CircularProgress, InputBase, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import useTurnstile from "../hooks/useTurnstile";
import useInscricaoForm, {
  formatarTelefone,
  validarEmail,
  validarNome,
  validarTelefone,
} from "../hooks/useInscricaoForm";
import { CORES } from "../utils/participante";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const BANNER = "/img/banner-encontro-associativismo-out2026-v2.jpg";
const DOURADO = "#a8842c";

const Campo = ({ label, erro, ...props }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography component="label" sx={{ display: "block", fontSize: 12.5, fontWeight: 600, color: CORES.texto, mb: 0.75 }}>
      {label}
    </Typography>
    <InputBase
      fullWidth
      {...props}
      sx={{
        height: 42,
        px: 1.5,
        fontSize: 14,
        backgroundColor: "#fff",
        border: `1px solid ${erro ? CORES.vermelho : "#d5dce4"}`,
        borderRadius: "6px",
        transition: "border-color .15s",
        "&.Mui-focused": { borderColor: erro ? CORES.vermelho : CORES.navyClaro, boxShadow: "0 0 0 3px rgba(18,58,92,0.08)" },
        "& input::placeholder": { color: "#98a3b0", opacity: 1 },
      }}
    />
    {erro && <Typography sx={{ fontSize: 11.5, color: CORES.vermelho, mt: 0.5 }}>{erro}</Typography>}
  </Box>
);

const InscricaoPublica = () => {
  const formulario = useInscricaoForm();
  const { nome, setNome, email, setEmail, telefone, setTelefone, aceiteLgpd, setAceiteLgpd, erros, setErros } =
    formulario;
  const { formularioValido, validarCampos, payload } = formulario;
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState("");
  const [esgotado, setEsgotado] = useState(false);

  // Se a consulta falhar, o formulario continua aberto — o backend e quem
  // de fato bloqueia quando o limite e atingido.
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/participantes/vagas`)
      .then(({ data }) => setEsgotado(!!data.esgotado))
      .catch(() => {});
  }, []);

  const { containerRef: turnstileRef, token: turnstileToken, reset: resetTurnstile } =
    useTurnstile(TURNSTILE_SITE_KEY);

  const pendencias = [
    !validarNome(nome) && "nome completo",
    !validarEmail(email) && "e-mail válido",
    !validarTelefone(telefone) && "WhatsApp",
    !aceiteLgpd && "autorização",
    !turnstileToken && "verificação",
  ].filter(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    if (!validarCampos()) return;

    setEnviando(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/participantes/inscricao-publica`, {
        ...payload(),
        turnstileToken,
      });
      setSucesso(true);
    } catch (error) {
      const mensagem = error.response?.data?.error || "Erro ao enviar sua inscrição. Tente novamente.";
      if (mensagem.startsWith("As inscrições estão encerradas")) setEsgotado(true);
      setErro(mensagem);
      resetTurnstile();
    } finally {
      setEnviando(false);
    }
  };

  const validarAoSair = (campo, valido, mensagem) =>
    setErros((prev) => ({ ...prev, [campo]: valido ? "" : mensagem }));

  return (
    <Box
      sx={{
        flex: 1,
        width: "100%",
        minHeight: "100vh",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 0, md: 6 },
        px: { xs: 0, md: 3 },
        background: "radial-gradient(circle at 30% 0%, #123a5c 0%, #0b2a45 50%, #061826 100%)",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          borderRadius: { xs: 0, md: "16px" },
          overflow: "hidden",
          boxShadow: { md: "0 30px 80px rgba(0,0,0,0.35)" },
        }}
      >
        {/* Banner sobre o proprio banner desfocado */}
        <Box
          sx={{
            position: "relative",
            width: { xs: "100%", md: "44%" },
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            py: { xs: 4, md: 6 },
            px: 3,
            backgroundColor: "#2b3a4a",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: -40,
              backgroundImage: `url(${BANNER})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(28px) brightness(0.6) saturate(0.8)",
            }}
          />
          <Box
            component="img"
            src={BANNER}
            alt="Grande Encontro do Associativismo com Flávio Bolsonaro — 13/10/2026, 17h, Clube Atlético Monte Líbano"
            sx={{
              position: "relative",
              display: "block",
              width: "100%",
              maxWidth: { xs: 280, md: 304 },
              borderRadius: "8px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
            }}
          />
        </Box>

        {/* Formulario */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "#fff",
            px: { xs: 2.5, sm: 5 },
            py: { xs: 4, md: 5 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: DOURADO, textTransform: "uppercase" }}>
            Inscrição gratuita · Vagas limitadas
          </Typography>
          <Typography
            component="h1"
            sx={{ fontSize: { xs: 24, sm: 27 }, fontWeight: 800, color: CORES.navy, lineHeight: 1.15, mt: 1, maxWidth: 320 }}
          >
            Grande Encontro do Associativismo
          </Typography>
          <Typography sx={{ fontSize: 14, color: CORES.textoSuave, mt: 0.75 }}>Com Flávio Bolsonaro</Typography>

          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: "8px",
              backgroundColor: "#faf7f0",
              border: "1px solid #ede4d0",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 1.25 }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: DOURADO, mt: 0.25 }} />
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CORES.navy }}>13 de outubro</Typography>
                <Typography sx={{ fontSize: 12, color: CORES.textoSuave }}>Terça-feira, às 17h</Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1.25 }}>
              <PlaceOutlinedIcon sx={{ fontSize: 19, color: DOURADO, mt: 0.25 }} />
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: CORES.navy, lineHeight: 1.3 }}>
                  Clube Atlético Monte Líbano
                </Typography>
                <Typography sx={{ fontSize: 12, color: CORES.textoSuave }}>Av. República do Líbano, 2267 · Moema</Typography>
              </Box>
            </Box>
          </Box>

          {sucesso ? (
            <Box sx={{ textAlign: "center", py: 5, flex: 1 }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 52, color: CORES.verde }} />
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: CORES.navy, mt: 1.5 }}>Inscrição confirmada!</Typography>
              <Typography sx={{ fontSize: 14, color: CORES.textoSuave, mt: 1, maxWidth: 340, mx: "auto" }}>
                Enviamos os detalhes e o seu QR Code de entrada para <strong>{email}</strong>. Apresente-o na recepção do
                evento.
              </Typography>
            </Box>
          ) : esgotado ? (
            <Box sx={{ textAlign: "center", py: 5, flex: 1 }}>
              <EventBusyOutlinedIcon sx={{ fontSize: 48, color: DOURADO }} />
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: CORES.navy, mt: 1.5 }}>Inscrições encerradas</Typography>
              <Typography sx={{ fontSize: 14, color: CORES.textoSuave, mt: 1, maxWidth: 340, mx: "auto" }}>
                Todas as vagas para o Grande Encontro do Associativismo foram preenchidas. Obrigado pelo interesse!
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
              <Campo
                label="Nome completo"
                placeholder="Como deve aparecer no crachá"
                autoComplete="name"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                onBlur={() => nome && validarAoSair("nome", validarNome(nome), "Digite nome e sobrenome.")}
                erro={erros.nome}
                inputProps={{ maxLength: 255 }}
              />

              <Box>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                  <Campo
                    label="E-mail"
                    type="email"
                    placeholder="voce@empresa.com.br"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => email && validarAoSair("email", validarEmail(email), "E-mail inválido.")}
                    erro={erros.email}
                    inputProps={{ maxLength: 255 }}
                  />
                  <Campo
                    label="WhatsApp"
                    type="tel"
                    placeholder="(11) 90000-0000"
                    autoComplete="tel-national"
                    value={telefone}
                    onChange={(e) => setTelefone(formatarTelefone(e.target.value))}
                    onBlur={() =>
                      telefone && validarAoSair("telefone", validarTelefone(telefone), "Use DDD + número.")
                    }
                    erro={erros.telefone}
                    inputProps={{ inputMode: "numeric", maxLength: 15 }}
                  />
                </Box>
                <Typography sx={{ fontSize: 12, color: CORES.textoSuave, mt: 1 }}>
                  Seu QR Code de acesso será enviado por e-mail.
                </Typography>
              </Box>

              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  p: 1.25,
                  pr: 2,
                  borderRadius: "8px",
                  backgroundColor: "#f4f6f9",
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  size="small"
                  checked={aceiteLgpd}
                  onChange={(e) => setAceiteLgpd(e.target.checked)}
                  sx={{ p: 0.5 }}
                />
                <Typography sx={{ fontSize: 12.5, color: "#3d4b5a", lineHeight: 1.5, pt: 0.4 }}>
                  Autorizo o uso dos meus dados para comunicações da ACSP.
                </Typography>
              </Box>

              <Box ref={turnstileRef} sx={{ minHeight: 65 }} />

              {erro && (
                <Typography
                  role="alert"
                  sx={{
                    fontSize: 13,
                    color: "#7f1d1d",
                    backgroundColor: CORES.vermelhoFundo,
                    borderRadius: "6px",
                    px: 1.5,
                    py: 1,
                  }}
                >
                  {erro}
                </Typography>
              )}

              <Button
                type="submit"
                disabled={enviando || !formularioValido || !turnstileToken}
                endIcon={!enviando && <ArrowForwardIcon sx={{ fontSize: "18px !important" }} />}
                sx={{
                  height: 46,
                  fontSize: 15,
                  fontWeight: 700,
                  borderRadius: "8px",
                  color: "#fff",
                  backgroundColor: CORES.navyClaro,
                  "&:hover": { backgroundColor: CORES.navy },
                  "&.Mui-disabled": { backgroundColor: "#e6e9ee", color: "#97a2ae" },
                }}
              >
                {enviando ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Confirmar inscrição"}
              </Button>

              {pendencias.length > 0 && !enviando && (
                <Typography sx={{ fontSize: 12, color: CORES.textoSuave, textAlign: "center", mt: -0.5 }}>
                  Falta: {pendencias.join(", ")}.
                </Typography>
              )}
            </Box>
          )}

          <Box
            sx={{
              mt: 3,
              pt: 2.5,
              borderTop: `1px solid ${CORES.borda}`,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                border: `1.5px solid ${CORES.navy}`,
                color: CORES.navy,
                display: "grid",
                placeItems: "center",
                fontSize: 8,
                fontWeight: 800,
              }}
            >
              ACSP
            </Box>
            <Typography sx={{ fontSize: 12, color: CORES.textoSuave }}>
              Realização: Associação Comercial de São Paulo
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InscricaoPublica;
